"use client";

import { useState, useEffect, useRef } from "react";
import { callApi } from "../common/Apicall";
import toast from "react-hot-toast";
import {
  FaCheck,
  FaTimes,
  FaPlus,
  FaTrash,
  FaSave,
  FaUndo,
  FaCheckCircle,
  FaEdit,
} from "react-icons/fa";
import Progress from "./Progress";

// Updated CustomTextArea component with calc(100vh - 220px) height
const CustomTextArea = ({
  label,
  inputType,
  type,
  maxLength,
  maxHeight = "calc(100vh - 220px)", // Use viewport-based calculation
  onAdd, // New prop to detect when item is added
  ...props
}) => {
  const textareaRef = useRef(null);

  // Reset textarea height when onAdd changes (item was added)
  useEffect(() => {
    if (textareaRef.current && onAdd) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = "2.5rem"; // Reset to approximately 1 row
    }
  }, [onAdd]);

  // Auto-resize textarea based on content with max height constraint
  const handleTextareaChange = (e) => {
    if (props.onChange) {
      props.onChange(e);
    }

    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = "auto";

    // Calculate the new height with max constraint
    // For viewport-based maxHeight, we need to calculate it dynamically
    const viewportHeight = window.innerHeight;
    const calculatedMaxHeight = viewportHeight - 220;
    const newHeight = Math.min(textarea.scrollHeight, calculatedMaxHeight);

    // Set the new height
    textarea.style.height = `${newHeight}px`;
  };

  return (
    <div
      className={`flex ${
        ["radio", "checkbox"].includes(type)
          ? "flex flex-row-reverse"
          : "flex-col"
      }`}
    >
      {label && (
        <label
          htmlFor={label}
          className={`${
            ["radio", "checkbox"].includes(type)
              ? "ml-2 font-sans text-sm"
              : "font-sans mb-1 text-sm"
          }`}
        >
          {label}
        </label>
      )}

      <textarea
        ref={textareaRef}
        id={label}
        type={type || "text"}
        maxLength={maxLength}
        {...props}
        onChange={handleTextareaChange}
        className="outline-none px-2 py-1 rounded-md border resize-y min-h-[2.5rem] overflow-y-auto"
        style={{
          transition: "height 0.2s ease",
          maxHeight: maxHeight, // Use the calculated max height
        }}
      />
    </div>
  );
};

// Main TodoDrawer component
export default function TodoDrawer({ isOpen, setIsOpen }) {
  const [todos, setTodos] = useState([]);
  const [newTodo, setNewTodo] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editedText, setEditedText] = useState("");
  const [loading, setLoading] = useState(false);
  const [addingTodo, setAddingTodo] = useState(false);
  const [itemAdded, setItemAdded] = useState(false); // To trigger textarea reset
  const [updatingStatus, setUpdatingStatus] = useState(null); // Track which todo is having status updated
  const [updatingTodo, setUpdatingTodo] = useState(null); // Track which todo is being updated
  const [deletingTodo, setDeletingTodo] = useState(null); // Track which todo is being deleted
  const URL = process.env.REACT_APP_URL;

  // Calculate the number of completed tasks
  const completedCount = todos?.filter((todo) => todo.status === 1)?.length;
  const totalTasks = todos?.length;
  const progress = totalTasks > 0 ? (completedCount / totalTasks) * 100 : 0;

  const addTodo = (e) => {
    e.preventDefault();
    if (!newTodo.trim()) {
      toast.error("Subject is required!");
      return;
    }

    setAddingTodo(true);

    const payload = {
      description: newTodo,
      status: 0,
    };

    callApi(URL + "/ToDoList/AddToDo", "POST", payload)
      .then((res) => {
        if (res && res.data) {
          setTodos((prevTodos) => [
            ...(Array.isArray(prevTodos) ? prevTodos : []),
            { id: res.data.id, description: newTodo, status: 0 },
          ]);
          toast.success("Item added successfully!");
          setNewTodo("");
          setItemAdded((prev) => !prev); // Toggle to trigger textarea reset
        } else {
          throw new Error("Invalid response from server");
        }
      })
      .catch((err) => {
        console.error("Error adding todo:", err);
        toast.error(err.message || "Something went wrong. Please try again.");
      })
      .finally(() => {
        setAddingTodo(false);
      });
  };

  const updateTodoStatus = (id, currentStatus) => {
    setUpdatingStatus(id);
    const newStatus = currentStatus === 0 ? 1 : 0;

    const updatedTodos = todos.map((todo) =>
      todo.id === id ? { ...todo, status: newStatus } : todo
    );

    setTodos(updatedTodos);

    const payload = {
      description: updatedTodos.find((todo) => todo.id === id).description,
      status: newStatus,
    };

    callApi(`${URL}/ToDoList/UpdateToDoItem/${id}`, "PUT", payload)
      .then((res) => {
        toast.success("Status updated successfully!");
      })
      .catch((err) => {
        toast.error(err);
        // Revert the change if the API call fails
        setTodos(todos);
      })
      .finally(() => {
        setUpdatingStatus(null);
      });
  };

  const startEditing = (id, description) => {
    setEditingId(id);
    setEditedText(description);
  };

  const saveEditedTodo = (id) => {
    if (!editedText.trim()) {
      toast.error("Subject is required!");
      return;
    }

    setUpdatingTodo(id);

    const updatedTodos = todos.map((todo) =>
      todo.id === id ? { ...todo, description: editedText } : todo
    );

    setTodos(updatedTodos);
    setEditingId(null);

    const payload = {
      description: editedText,
      status: updatedTodos.find((todo) => todo.id === id)?.status || 0,
    };

    callApi(`${URL}/ToDoList/UpdateToDoItem/${id}`, "PUT", payload)
      .then(() => {
        toast.success("Todo updated successfully!");
      })
      .catch((err) => {
        toast.error(err || "Something went wrong!");
        // Revert the change if the API call fails
        setTodos(todos);
        setEditingId(id);
      })
      .finally(() => {
        setUpdatingTodo(null);
      });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditedText("");
  };

  const deleteTodo = (id) => {
    setDeletingTodo(id);

    callApi(`${URL}/ToDoList/DeleteToDoItem/${id}`, "DELETE")
      .then((res) => {
        toast.success("Item deleted successfully!");
        setTodos(todos.filter((todo) => todo.id !== id));
      })
      .catch((err) => {
        const errorMessage = err?.message || "Something went wrong!";
        toast.error(errorMessage);
      })
      .finally(() => {
        setDeletingTodo(null);
      });
  };

  // The drawer is mounted (closed) by the Header on every screen, so fetching
  // on mount meant a GetToDoItems call on each route change for a list nobody
  // was looking at. It now loads when the drawer is actually opened, which
  // also means the list is fresh each time it is opened.
  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    const getToDoItems = async () => {
      setLoading(true);

      try {
        const res = await callApi(`${URL}/ToDoList/GetToDoItems`, "GET");
        if (!cancelled) setTodos(res?.data);
      } catch (error) {
        console.error("Error fetching list:", error || "Something went wrong!");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    getToDoItems();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <div>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsOpen(false)}
        ></div>
      )}

      <div
    className={`fixed inset-y-0 right-0 w-full sm:w-1/3 bg-white shadow-lg transform ${
  isOpen ? "translate-x-0" : "translate-x-full"
} transition-transform duration-300 ease-in-out z-50`}
      >
        <div className="p-6 relative h-full flex flex-col">
          {/* Close Button */}
          <button
            className="absolute top-4 right-4 p-2 bg-gray-300 rounded-full hover:bg-gray-400"
            onClick={() => setIsOpen(false)}
          >
            <FaTimes />
          </button>

          <h2 className="text-2xl font-bold mb-6">To-Do List</h2>

          {/* Progress Bar and Status Text */}
          <div className="space-y-2 mb-6">
            <div className="relative">
              <Progress
                value={progress}
                className="h-2 [&>div]:bg-green-700 bg-slate-200/20"
              />
            </div>
            <p className="text-sm text-slate-500">
              {completedCount || 0} of {totalTasks || 0} tasks completed
            </p>
          </div>

          <form onSubmit={addTodo} className="mb-6">
            <div className="w-full ">
              <CustomTextArea
                name="subject"
                placeholder="Enter your to-do task..."
                value={newTodo}
                onChange={(e) => setNewTodo(e.target.value)}
                maxHeight="calc(100vh - 220px)" // Use viewport-based calculation
                onAdd={itemAdded} // Pass the reset trigger
                disabled={addingTodo} // Disable textarea while adding
              />
            </div>
            <button
              type="submit"
              disabled={addingTodo}
              className={`mt-3 w-full p-3 rounded-lg transition-colors flex items-center justify-center ${
                addingTodo
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-500 hover:bg-blue-600 text-white"
              }`}
            >
              {addingTodo ? (
                <>
                  <div className="animate-spin border-2 border-white border-t-transparent w-4 h-4 rounded-full mr-2"></div>
                  Adding...
                </>
              ) : (
                <>
                  <FaPlus className="mr-2" /> Add Todo
                </>
              )}
            </button>
          </form>

          {/* Loading State */}
          {loading ? (
            <div className="flex justify-center items-center">
              <div className="animate-spin border-4 border-blue-500 border-t-transparent w-8 h-8 rounded-full"></div>
            </div>
          ) : todos?.length === 0 ? (
            <p className="text-center text-gray-500 mt-4">
              Add your first task above ✨
            </p>
          ) : (
            <ul className="space-y-3 max-h-[calc(100vh-200px)] overflow-y-auto">
              {todos
                ?.slice(0)
                ?.reverse()
                ?.map((todo) => (
                  <li
                    key={todo.id}
                    className={`flex items-start justify-between p-4 rounded-lg transition-all duration-200 ${
                      todo.status === 1
                        ? "bg-green-50 border-2 border-green-200 shadow-sm"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                  >
                    {editingId === todo.id ? (
                      <div className="w-full ">
                        <CustomTextArea
                          name="subject"
                          autoFocus
                          value={editedText}
                          onChange={(e) => setEditedText(e.target.value)}
                          maxHeight="calc(100vh - 220px)" // Use viewport-based calculation for editing
                          disabled={updatingTodo === todo.id} // Disable while updating
                        />
                      </div>
                    ) : (
                      <div
                        className={`flex-grow  relative ${
                          todo.status === 1
                            ? "text-green-800 font-medium"
                            : "text-gray-800"
                        }`}
                      >
                        {todo.status === 1 && (
                          <div className="flex items-center gap-2 mb-1">
                            <FaCheckCircle className="text-green-600 w-4 h-4" />
                            <span className="text-xs font-semibold text-green-600 bg-green-100 px-2 py-1 rounded-full">
                              COMPLETED
                            </span>
                          </div>
                        )}
                        <div
                          className="whitespace-pre-wrap [overflow-wrap:anywhere]"
                          style={{ whiteSpace: "pre-wrap" }}
                        >
                          {todo.description}
                        </div>
                      </div>
                    )}

                    <div className="flex items-center space-x-3 flex-shrink-0 ml-3">
                      {editingId === todo.id ? (
                        <>
                          <button
                            onClick={() => saveEditedTodo(todo.id)}
                            disabled={updatingTodo === todo.id}
                            className={`p-2 rounded-md transition-colors ${
                              updatingTodo === todo.id
                                ? "bg-blue-300 cursor-not-allowed"
                                : "bg-blue-500 hover:bg-blue-600 text-white"
                            }`}
                            title="Save changes"
                          >
                            {updatingTodo === todo.id ? (
                              <div className="animate-spin border-2 border-white border-t-transparent w-4 h-4 rounded-full"></div>
                            ) : (
                              <FaSave />
                            )}
                          </button>
                          <button
                            onClick={cancelEditing}
                            disabled={updatingTodo === todo.id}
                            className={`p-2 rounded-md transition-colors ${
                              updatingTodo === todo.id
                                ? "bg-gray-300 cursor-not-allowed"
                                : "bg-gray-500 hover:bg-gray-600 text-white"
                            }`}
                            title="Cancel editing"
                          >
                            <FaTimes />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() =>
                              updateTodoStatus(todo.id, todo.status)
                            }
                            disabled={updatingStatus === todo.id || deletingTodo === todo.id || updatingTodo}
                            className={`p-2 rounded-md transition-colors ${
                              updatingStatus === todo.id || deletingTodo === todo.id || updatingTodo
                                ? "cursor-not-allowed opacity-50"
                                : todo.status === 0
                                ? "bg-green-500 hover:bg-green-600"
                                : "bg-yellow-500 hover:bg-yellow-600"
                            } text-white`}
                            title={
                              todo.status === 0 ? "Mark as done" : "Reopen task"
                            }
                          >
                            {updatingStatus === todo.id ? (
                              <div className="animate-spin border-2 border-white border-t-transparent w-4 h-4 rounded-full"></div>
                            ) : todo.status === 0 ? (
                              <FaCheck />
                            ) : (
                              <FaUndo />
                            )}
                          </button>
                          <button
                            onClick={() =>
                              startEditing(todo.id, todo.description)
                            }
                            disabled={updatingStatus === todo.id || deletingTodo === todo.id || updatingTodo}
                            className={`p-2 rounded-md transition-colors ${
                              updatingStatus === todo.id || deletingTodo === todo.id || updatingTodo
                                ? "bg-blue-300 cursor-not-allowed"
                                : "bg-blue-500 hover:bg-blue-600 text-white"
                            }`}
                            title="Edit task"
                          >
                            <FaEdit />
                          </button>

                          <button
                            onClick={() => deleteTodo(todo.id)}
                            disabled={deletingTodo === todo.id || updatingStatus === todo.id || updatingTodo}
                            className={`p-2 rounded-md transition-colors ${
                              deletingTodo === todo.id || updatingStatus === todo.id || updatingTodo
                                ? "bg-red-300 cursor-not-allowed"
                                : "bg-red-500 hover:bg-red-600 text-white"
                            }`}
                            title="Delete task"
                          >
                            {deletingTodo === todo.id ? (
                              <div className="animate-spin border-2 border-white border-t-transparent w-4 h-4 rounded-full"></div>
                            ) : (
                              <FaTrash />
                            )}
                          </button>
                        </>
                      )}
                    </div>
                  </li>
                ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}